import { useState } from 'react';
import cx from 'classnames';
import Button from '../../components/Button';
import ToggleSlider from '../../components/ToggleSlider';
import { Dialog, DialogContent, DialogTitle } from '../../components/Dialog';
import { GRADE_LABELS } from '../../ConcerningEmotionsReportUtilities';
import { getInitial } from '../../GlobalFunctions';
import { MOODBOARD_ORDER, MOODBOARD_ORDER_6_12 } from '../../StudentDetailPanelData';
import { getElementaryMoodColorFill, getSecondaryMoodColorFill } from '../MoodboardAdmin/moodboardUtilities';
import WeeklyClassSnapshot from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import SchoolYearSummary from '../SchoolYearSummary/SchoolYearSummary';
import StudentsWhoMayNeedSupport from '../StudentsWhoMayNeedSupport/StudentsWhoMayNeedSupport';
import type { GradeBand } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import DailyCheckInsTable, { DayCell } from '../DailyCheckInsTable/DailyCheckInsTable';
import type { MoodboardStudent } from '../DailyCheckInsTable/DailyCheckInsTable';
import tableStyles from '../DailyCheckInsTable/DailyCheckInsTable.module.scss';
import { formatWeekLabel, getMondayWeekBounds, getWeeklySnapshot } from '../moodboardSnapshot';
import type { SnapshotCheckIn } from '../moodboardSnapshot';
import { addDays, getStudentWeek, getWeekDays, getWeekNavigation } from '../moodboardWeek';
import styles from './ClassroomMoodboard.module.scss';

interface ClassroomMoodboardProps {
  gradeBand: GradeBand;
  classroomId: string;
  classroomName: string;
  grade: number;
  students: MoodboardStudent[];
  checkIns: SnapshotCheckIn[];
}

const LEGEND = {
  elementary: { moods: MOODBOARD_ORDER, color: getElementaryMoodColorFill },
  secondary: { moods: MOODBOARD_ORDER_6_12, color: getSecondaryMoodColorFill },
};

const fullPageClassName = '!inset-[24px] !w-auto !max-h-none !translate-x-0 !translate-y-0 !p-[3.2rem]';

const ClassroomMoodboard = ({
  gradeBand,
  classroomId,
  classroomName,
  grade,
  students,
  checkIns,
}: ClassroomMoodboardProps) => {
  const [weekStart, setWeekStart] = useState(() => getMondayWeekBounds(new Date())[0]);
  const [isFullScreen, setFullScreen] = useState(false);
  const [isHideNames, setHideNames] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<MoodboardStudent>();

  const isElementary = gradeBand === 'elementary';
  const weekEnd = addDays(weekStart, 7);
  const weekLabel = formatWeekLabel(weekStart, weekEnd);
  const { hasPrevious, hasNext } = getWeekNavigation(checkIns, weekStart, Date.now());
  const snapshot = getWeeklySnapshot({
    checkIns,
    rosterUserIds: students.map((student) => student.id),
    weekStart,
    weekEnd,
  });
  const days = getWeekDays(weekStart);
  const legend = LEGEND[gradeBand];

  return (
    <div className={styles.container}>
      <div className={styles.reportHead}>
        <div>
          <h1 className={styles.title}>Moodboard</h1>
          <div className={styles.meta}>
            {classroomName} · {GRADE_LABELS[grade]} · {students.length} students
          </div>
        </div>
        <Button small orange={isElementary} darkBlue={!isElementary} onClick={() => setFullScreen(true)}>
          ⛶ Full screen
        </Button>
      </div>

      <section className={styles.section}>
        <WeeklyClassSnapshot
          snapshot={snapshot}
          gradeBand={gradeBand}
          week={{
            label: weekLabel,
            onPrevious: hasPrevious ? () => setWeekStart(addDays(weekStart, -7)) : undefined,
            onNext: hasNext ? () => setWeekStart(weekEnd) : undefined,
          }}
        />
        <div className={styles.tableHead}>
          <div className={styles.legend}>
            {legend.moods.map((mood) => (
              <span key={mood}>
                <i style={{ backgroundColor: legend.color(mood) }} />
                {mood}
              </span>
            ))}
          </div>
          <span>Select a student to open their Student Report.</span>
        </div>
        <DailyCheckInsTable
          students={students}
          checkIns={checkIns}
          weekStart={weekStart}
          gradeBand={gradeBand}
          onSelectStudent={(id) => setSelectedStudent(students.find((student) => student.id === id))}
        />
      </section>

      <StudentsWhoMayNeedSupport classroomId={classroomId} gradeBand={gradeBand} />
      <SchoolYearSummary gradeBand={gradeBand} checkIns={checkIns} />

      <Dialog open={isFullScreen} onOpenChange={setFullScreen}>
        <DialogContent className={fullPageClassName}>
          <div className={styles.reportHead}>
            <div>
              <DialogTitle className={styles.fullScreenTitle}>{classroomName}</DialogTitle>
              <div className={styles.meta}>Moodboard · {weekLabel}</div>
            </div>
            <div className={styles.actions}>
              <ToggleSlider
                isChecked={isHideNames}
                onToggle={() => setHideNames(!isHideNames)}
                onBackgroundColor={isElementary ? '#ff6319' : '#012a73'}
                offBackgroundColor='#d8dde3'
                switchColor='#ffffff'
              />
              Hide student names
              <Button small orange={isElementary} darkBlue={!isElementary} onClick={() => setFullScreen(false)}>
                Exit full screen
              </Button>
            </div>
          </div>
          <DailyCheckInsTable
            students={students}
            checkIns={checkIns}
            weekStart={weekStart}
            gradeBand={gradeBand}
            hideNames={isHideNames}
            isLarge
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedStudent)} onOpenChange={(isOpen) => !isOpen && setSelectedStudent(undefined)}>
        {selectedStudent && (
          <DialogContent className='!top-[24px] !w-[min(calc(100vw-48px),1000px)] !translate-y-0 !p-[3.2rem]'>
            <div className={styles.reportHead}>
              <div className={styles.student}>
                <span className={cx(tableStyles.avatar, styles.largeAvatar)} aria-hidden='true'>
                  {getInitial(selectedStudent.name)}
                </span>
                <div>
                  <div className={styles.crumbs}>
                    <button type='button' onClick={() => setSelectedStudent(undefined)}>
                      Moodboard
                    </button>{' '}
                    › Student Report
                  </div>
                  <DialogTitle asChild>
                    <h1 className={styles.title}>{selectedStudent.name}</h1>
                  </DialogTitle>
                  <div className={styles.meta}>
                    {classroomName} · {GRADE_LABELS[grade]}
                  </div>
                </div>
              </div>
              <Button
                small
                orange={isElementary}
                darkBlue={!isElementary}
                onClick={() => setSelectedStudent(undefined)}
              >
                ‹ Back to Moodboard
              </Button>
            </div>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Mood check-ins</h2>
              <p className={styles.meta}>{weekLabel}</p>
              <div className={`${styles.studentWeek} ${tableStyles.large}`}>
                {getStudentWeek(checkIns, selectedStudent.id, weekStart).map((dayCheckIns, i) => (
                  <div key={i}>
                    <div className={styles.day}>
                      <b>{days[i].toLocaleDateString('en-US', { weekday: 'short' })}</b>
                      {days[i].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <DayCell checkIns={dayCheckIns} dayIndex={i} gradeBand={gradeBand} />
                  </div>
                ))}
              </div>
            </section>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default ClassroomMoodboard;
